import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col
} from 'reactstrap';
import axios from 'axios';

const PrivacyPolicy = () => {
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch Policy data from backend using slug
  const fetchPolicyData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get('/api/public/policy/privacy-policy');

      if (response.data.isOk && response.data.data) {
        setPolicyData(response.data.data);
      } else {
        setError('Failed to fetch policy data');
      }
    } catch (error) {
      console.error('Error fetching privacy policy data:', error);
      setError('Error loading privacy policy content');
      setPolicyData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicyData();
  }, []);

  // Loading state
  if (loading) {
    return (
      <section className="about-section privacy-policy-section">
        <Container>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="spinner-border text-danger" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  // Error / no data fallback - show static content
  const title = policyData?.title || 'Privacy Policy';
  const description = policyData?.description || null;

  return (
    <section className="about-section privacy-policy-section">
      <Container>
        <Row>
          <Col lg={12}>
            {/* Title — dynamic from backend, same style as original */}
            <p className="text-uppercase mb-4 text-center privacy-policy-subtitle">
              {title}
            </p>

            {/* Description — rendered as rich HTML from Jodit Editor */}
            {description ? (
              <div
                className="about-text"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            ) : (
              // Fallback static content if no backend data
              <div className="about-text">
                <div className="about-text display-4 mb-4 text-center">
                  <p>
                    Welcome to ApexCXOs. We are committed to protecting the privacy, confidentiality, and
                    trust of our members, visitors, and partners. This Privacy Policy explains how we collect,
                    use, disclose, and safeguard personal data when you access our website, platforms, events,
                    or services.
                  </p>
                </div>

                <h4 className="text-black mb-3">1. Scope of This Policy</h4>
                <p>This policy applies to all interactions with ApexCXOs, including:</p>
                <ul className="mb-4">
                  <li>Visitors to the ApexCXOs Community website.</li>
                  <li>Registered members and membership applicants.</li>
                  <li>Event participants (both virtual and in-person).</li>
                  <li>Contributors, speakers, and partners.</li>
                </ul>
                <p className="fst-italic mb-5">
                  Note: This policy does not apply to third-party websites or services linked to our platform.
                </p>

                <h4 className="text-black mb-3">2. Personal Data Collection</h4>
                <p>We collect Personally Identifiable Information (PII) to provide a tailored professional experience.</p>

                <h5 className="text-black mt-3 mb-2">A. Information You Provide</h5>
                <p>We collect data through registration forms, surveys, program applications, and event sign-ups, including:</p>
                <ul className="mb-3">
                  <li><strong>Identity:</strong> Full name and job title.</li>
                  <li><strong>Contact Details:</strong> Email address and physical address.</li>
                  <li><strong>Professional Profile:</strong> Industry insights, membership level, and professional interests.</li>
                </ul>

                <h5 className="text-black mt-3 mb-2">B. Automated Collection</h5>
                <ul className="mb-3">
                  <li><strong>Browsing:</strong> We do not collect PII during casual browsing unless voluntarily provided.</li>
                  <li><strong>Analytics:</strong> Standard technical data (IP address, browser type, and usage patterns) is collected to improve site performance.</li>
                </ul>

                <h5 className="text-black mt-3 mb-2">C. Participation Data</h5>
                <p className="mb-5">We collect records of your attendance at workshops, roundtables, and webinars, as well as any feedback or survey responses provided during these sessions.</p>

                <h4 className="text-black mb-3">3. How We Use Your Data</h4>
                <p>ApexCXOs processes your data to maintain the integrity of our executive network:</p>
                <ul className="mb-5">
                  <li><strong>Management:</strong> Processing memberships and managing account participation.</li>
                  <li><strong>Events:</strong> Organizing and executing professional development programs.</li>
                  <li><strong>Communication:</strong> Sharing community updates, insights, and newsletters.</li>
                  <li><strong>Networking:</strong> Facilitating peer-to-peer collaboration and knowledge sharing.</li>
                </ul>

                <h4 className="text-black mb-3">4. Legal Basis for Processing</h4>
                <p>Our processing is grounded in the following legal frameworks:</p>
                <ul className="mb-5">
                  <li><strong>Consent:</strong> Where you have given explicit permission.</li>
                  <li><strong>Contractual Necessity:</strong> To fulfill membership benefits and event registrations.</li>
                  <li><strong>Legitimate Interests:</strong> To operate and grow a professional CXO community.</li>
                </ul>

                <h4 className="text-black mb-3">5. Data Sharing and Disclosure</h4>
                <p>We maintain strict controls over how your data is shared.</p>

                <h5 className="text-black mt-3 mb-2">A. Affiliates & Partners</h5>
                <ul className="mb-3">
                  <li><strong>Affiliated Organizations:</strong> ApexCXOs reserves the right to share your information with affiliated organizations without requiring prior individual permission.</li>
                  <li><strong>Event Sponsors:</strong> PII submitted during event registration may be shared with sponsoring vendors, partners, and affiliates associated with that specific event.</li>
                </ul>

                <h5 className="text-black mt-3 mb-2">B. Service Providers</h5>
                <p>We share data with trusted third parties (hosting, email marketing, event platforms) solely to facilitate our services.</p>

                <h5 className="text-black mt-3 mb-2">C. Protections Against Sale</h5>
                <p>ApexCXOs does not sell, exchange, or transfer your information to outside companies for marketing purposes without your consent, except:</p>
                <ul className="mb-5">
                  <li>When required by law (search warrants, court orders).</li>
                  <li>During national exigent circumstances.</li>
                </ul>

                <h4 className="text-black mb-3">6. Data Retention & Security</h4>
                <ul className="mb-5">
                  <li><strong>Retention:</strong> We retain membership data while your account is active. Event and legal data are retained as long as necessary for reporting and compliance.</li>
                  <li><strong>Security:</strong> We implement technical measures including role-based access controls and secure hosting environments to prevent unauthorized access.</li>
                </ul>

                <h4 className="text-black mb-3">7. Your Privacy Rights</h4>
                <p>Depending on your jurisdiction, you may have the right to:</p>
                <ul className="mb-5">
                  <li>Access or request a copy of your data.</li>
                  <li>Correct or update inaccurate information.</li>
                  <li>Request Deletion (the "Right to Erasure").</li>
                  <li>Object to or restrict specific processing activities.</li>
                </ul>

                <h4 className="text-black mb-3">8. Community Conduct & Confidentiality</h4>
                <p>As a member of a professional CXO community, you agree to:</p>
                <ul className="mb-5">
                  <li>Respect the confidentiality of discussions within the forum.</li>
                  <li>Refrain from misusing the personal data of other members.</li>
                  <li>Acknowledge that we may moderate content to ensure compliance and privacy.</li>
                </ul>

                <h4 className="text-black mb-3">Contact Us</h4>
                <p>For questions, comments, or to exercise your privacy rights, please contact us at:</p>
                <p>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:admin@apexcxos.com" className="text-decoration-none" style={{ color: '#e60000' }}>
                    admin@apexcxos.com
                  </a>
                </p>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default PrivacyPolicy;
